/**
 * /affiliate-kit?code=XYZ — public partner marketing kit.
 *
 * Pulls /api/affiliate/kit/{code} and renders ready-to-share assets:
 *   - Tracking links (home, lifetime, comparison pages — all with ?ref=)
 *   - OG banner images (download/copy URL)
 *   - Copy templates (Twitter, LinkedIn, email)
 *   - Video script
 *   - Creator notes
 *
 * Removing friction here is the whole point: a partner shouldn't have to
 * think about appending ?ref= — every link/copy snippet ships pre-baked.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Copy, Check, Download, Link as LinkIcon, FileText, Video, ExternalLink, Loader2, Image as ImageIcon, Twitter, Linkedin, Send, Share2, Sparkles, Wand2, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { API } from "../config";

// Fire-and-forget tracker so the UI never blocks on the network round-trip.
// Errors are swallowed — a failed beacon must never break a download click.
function trackAssetEvent(code, assetId, event, channel) {
  if (!code || !assetId) return;
  try {
    axios.post(`${API}/affiliate/asset-event`, {
      code, asset_id: assetId, event, channel: channel || null,
    }).catch(() => {});
  } catch { /* ignore */ }
}

const COPY = {
  sk: {
    h1: "Marketing kit pre partnerov",
    sub: "Pripravené zdielateľné odkazy, bannery a copy texty so zabudovaným tvojím kódom.",
    no_code: "Otvor cez svoj dashboard alebo pridaj ?code=tvoj-kód do URL.",
    sec_links: "Tvoje odkazy",
    sec_banners: "Bannery",
    sec_copy: "Copy templates",
    sec_video: "Video script (30s)",
    sec_notes: "Tipy",
    sec_marketing: "Marketing balíček",
    sec_marketing_sub: "Hotové logá a reklamné kreatívy — stiahni a vlož do svojho príspevku.",
    tab_logos: "Logá",
    tab_creatives: "Reklamné kreatívy",
    copy: "Kopírovať", copied: "Skopírované!",
    download: "Stiahnuť",
    copy_url: "Kopírovať URL",
    share: "Zdieľať",
    personalize: "Personalizovať",
    personal_on: "S tvojím menom",
    personal_off: "Predvolené",
    sec_suggest: "Tipy pre teba",
    sec_suggest_segment: "Tieto kreatívy reálne zarábajú partnerom s podobným publikom",
    sec_suggest_global: "Top kreatívy v celej sieti — zoradené podľa skutočných provízií",
    sec_suggest_cold: "Najnovšie kreatívy — siet sa rozrastá",
    suggest_winner: "Top voľba",
    suggest_used: "Už si stiahol",
    suggest_earned_label: "zarobil",
    suggest_conv: "konv.",
    suggest_eps_tip: "Koľko € prinesie 1 share tejto kreatívy",
    coach_btn: "AI Copy Coach",
    coach_h: "AI Copy Coach",
    coach_for: "Pre kreatívu",
    coach_disclaimer: "Generované cez Claude Sonnet · Pripravené tipy v tvojom jazyku a tóne",
    coach_channel: "Platforma",
    coach_generate: "Generovať 3 varianty",
    coach_generating: "Generujem…",
    coach_regenerate: "Generovať znova",
    coach_quota_left: "Zostávajúcich generovaní dnes: {n}/10",
    coach_quota_exceeded: "Vyčerpal si dnešný limit (10/deň). Skús to znova zajtra.",
    coach_error: "Niečo sa pokazilo. Skús to znova o chvíľu.",
    open_dash: "Späť na dashboard",
  },
  en: {
    h1: "Affiliate Marketing Kit",
    sub: "Ready-made links, banners and copy with your code already baked in.",
    no_code: "Open via your dashboard or add ?code=your-code to the URL.",
    sec_links: "Your links",
    sec_banners: "Banners",
    sec_copy: "Copy templates",
    sec_video: "Video script (30s)",
    sec_notes: "Tips",
    sec_marketing: "Marketing pack",
    sec_marketing_sub: "Ready-made logos and ad creatives — download and drop into your post.",
    tab_logos: "Logos",
    tab_creatives: "Ad creatives",
    copy: "Copy", copied: "Copied!",
    download: "Download",
    copy_url: "Copy URL",
    share: "Share",
    personalize: "Personalize",
    personal_on: "With your name",
    personal_off: "Default",
    sec_suggest: "Picked for you",
    sec_suggest_segment: "These creatives actually earn money for partners with a similar audience",
    sec_suggest_global: "Top creatives across the network — ranked by real commissions",
    sec_suggest_cold: "Latest creatives — the network is growing",
    suggest_winner: "Top pick",
    suggest_used: "Already used",
    suggest_earned_label: "earned",
    suggest_conv: "conv.",
    suggest_eps_tip: "How much € one share of this creative brings in",
    coach_btn: "AI Copy Coach",
    coach_h: "AI Copy Coach",
    coach_for: "For creative",
    coach_disclaimer: "Powered by Claude Sonnet · Captions tailored to your audience and locale",
    coach_channel: "Platform",
    coach_generate: "Generate 3 variants",
    coach_generating: "Generating…",
    coach_regenerate: "Regenerate",
    coach_quota_left: "Generations left today: {n}/10",
    coach_quota_exceeded: "Daily limit reached (10/day). Try again tomorrow.",
    coach_error: "Something broke. Please try again in a moment.",
    open_dash: "Back to dashboard",
  },
};

export default function AffiliateKitPage() {
  const { language } = useLanguage();
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toLowerCase();
  const t = COPY[language] || COPY.en;
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!code) { setErr("no_code"); setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/affiliate/kit/${encodeURIComponent(code)}`)
      .then(r => { if (!cancelled) { setKit(r.data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e?.response?.data?.detail || "error"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [code]);

  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-8 h-8 text-zinc-500 animate-spin" /></div>;
  }
  if (err === "no_code" || !kit) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-zinc-300 mb-4">{t.no_code}</p>
          <Link to="/affiliate" className="text-[#FFE600] underline">{t.open_dash} →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="affiliate-kit-page">
      <header className="border-b border-zinc-800 py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-black text-lg font-['Cabinet_Grotesk']">TopRankPulse</Link>
          <Link to={`/affiliate?code=${code}`} className="text-xs text-zinc-400 hover:text-white">{t.open_dash} →</Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFE600] mb-3">{kit.partner.name} · /r/{kit.partner.code}</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">{t.h1}</h1>
        <p className="text-base text-zinc-400 mb-10 max-w-2xl">{t.sub}</p>

        {/* Links */}
        <Section icon={LinkIcon} title={t.sec_links} testid="kit-links">
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.entries(kit.links).map(([label, url]) => (
              <CopyRow key={label} label={label} url={url} t={t} testid={`kit-link-${label}`} />
            ))}
            {kit.comparison_links.map(c => (
              <CopyRow key={c.slug} label={`vs ${c.name}`} url={c.url} accent={c.color} t={t} testid={`kit-link-vs-${c.slug}`} />
            ))}
          </div>
        </Section>

        {/* Banners */}
        <Section icon={Download} title={t.sec_banners} testid="kit-banners">
          <div className="grid sm:grid-cols-2 gap-4">
            {kit.banners.map((b, i) => (
              <div key={i} className="bg-[#141414] border border-zinc-800 overflow-hidden" data-testid={`kit-banner-${i}`}>
                <img src={b.url} alt={b.alt} className="w-full aspect-[1200/630] object-cover bg-black" loading="lazy" />
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{b.label}</p>
                    <p className="text-[10px] text-zinc-500 font-mono">{b.size}</p>
                  </div>
                  <a href={b.url} download
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                    <Download className="w-3 h-3" /> {t.download}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Smart Suggestions — segment-aware top picks shown above the full gallery */}
        <SmartSuggestions code={code} t={t} shareUrl={kit.links?.short || `https://toprankpulse.com/r/${code}`} partnerName={kit.partner?.name || ""} twitterCopy={kit.copy_templates?.twitter?.[0] || ""} linkedinCopy={kit.copy_templates?.linkedin?.[0] || ""} />

        {/* Marketing pack — logos + pre-baked ad creatives */}
        {Array.isArray(kit.marketing_assets) && kit.marketing_assets.length > 0 && (
          <MarketingPack
            assets={kit.marketing_assets}
            t={t}
            code={code}
            shareUrl={kit.links?.short || `https://toprankpulse.com/r/${code}`}
            partnerName={kit.partner?.name || ""}
            twitterCopy={kit.copy_templates?.twitter?.[0] || ""}
            linkedinCopy={kit.copy_templates?.linkedin?.[0] || ""}
          />
        )}

        {/* Copy templates */}
        <Section icon={FileText} title={t.sec_copy} testid="kit-copy">
          <CopyGroup label="Twitter / X" items={kit.copy_templates.twitter} t={t} prefix="kit-tweet" />
          <CopyGroup label="LinkedIn" items={kit.copy_templates.linkedin} t={t} prefix="kit-linkedin" />
          <CopyGroup label="Email subject lines" items={kit.copy_templates.email_subject_lines} t={t} prefix="kit-subject" />
          <CopyArea label="Email body" content={kit.copy_templates.email_body} t={t} testid="kit-email-body" />
        </Section>

        {/* Video script */}
        <Section icon={Video} title={t.sec_video} testid="kit-video">
          <CopyArea label="" content={kit.video_script_30s} t={t} testid="kit-video-script" />
        </Section>

        {/* Creator notes */}
        <Section icon={ExternalLink} title={t.sec_notes} testid="kit-notes">
          <ul className="space-y-2">
            {kit.creator_notes.map((n, i) => (
              <li key={i} className="text-sm text-zinc-300 bg-[#141414] border border-zinc-800 p-3" data-testid={`kit-note-${i}`}>
                {n}
              </li>
            ))}
          </ul>
        </Section>
      </section>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="mb-10" data-testid={testid}>
      <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-black font-['Cabinet_Grotesk'] mb-4">
        <Icon className="w-5 h-5 text-[#FFE600]" /> {title}
      </h2>
      {children}
    </div>
  );
}

function MarketingPack({ assets, t, code, shareUrl, partnerName, twitterCopy, linkedinCopy }) {
  const [tab, setTab] = useState("logos");
  const logos = assets.filter(a => a.kind === "logo");
  const creatives = assets.filter(a => a.kind === "creative");
  const visible = tab === "logos" ? logos : creatives;
  return (
    <div className="mb-10" data-testid="kit-marketing">
      <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-black font-['Cabinet_Grotesk'] mb-1">
        <ImageIcon className="w-5 h-5 text-[#FFE600]" /> {t.sec_marketing}
      </h2>
      <p className="text-sm text-zinc-400 mb-4">{t.sec_marketing_sub}</p>

      <div className="inline-flex bg-[#141414] border border-zinc-800 mb-5">
        <button
          type="button"
          onClick={() => setTab("logos")}
          data-testid="kit-marketing-tab-logos"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${tab === "logos" ? "bg-[#FFE600] text-black" : "text-zinc-300 hover:text-white"}`}
        >
          {t.tab_logos} <span className="opacity-60">({logos.length})</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("creatives")}
          data-testid="kit-marketing-tab-creatives"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-l border-zinc-800 ${tab === "creatives" ? "bg-[#FFE600] text-black" : "text-zinc-300 hover:text-white"}`}
        >
          {t.tab_creatives} <span className="opacity-60">({creatives.length})</span>
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map(a => (
          <AssetCard
            key={a.id}
            asset={a}
            t={t}
            code={code}
            shareUrl={shareUrl}
            partnerName={partnerName}
            twitterCopy={twitterCopy}
            linkedinCopy={linkedinCopy}
          />
        ))}
      </div>
    </div>
  );
}

function buildShareLink(channel, postText, assetUrl) {
  const text = encodeURIComponent(postText || "");
  const url = encodeURIComponent(assetUrl || "");
  switch (channel) {
    case "x":
      return `https://twitter.com/intent/tweet?text=${text}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    case "whatsapp":
      return `https://wa.me/?text=${text}%20${url}`;
    case "telegram":
      return `https://t.me/share/url?url=${url}&text=${text}`;
    default:
      return null;
  }
}

function AssetCard({ asset, t, code, shareUrl, partnerName, twitterCopy, linkedinCopy }) {
  const [copied, setCopied] = useState(false);
  const [personalised, setPersonalised] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const canPersonalise = !!code && !!partnerName && asset.kind !== "logo";
  const canCoach = !!code && asset.kind !== "logo";

  // Personalised URLs route through /personal/{code} which renders a
  // partner-stamped variant on the fly (cached server-side after first hit).
  const previewUrl = personalised && canPersonalise
    ? `/api/affiliate/asset/${asset.id}/personal/${code}`
    : asset.preview_url;
  const downloadUrl = personalised && canPersonalise
    ? `/api/affiliate/asset/${asset.id}/personal/${code}?download=1`
    : asset.download_url;

  // Absolute URL for social shares — needs to land on the public domain
  // so the recipient's preview cards resolve correctly.
  const shareAssetUrl = `https://toprankpulse.com${personalised && canPersonalise
    ? `/api/affiliate/asset/${asset.id}/personal/${code}`
    : `/api/affiliate/asset/${asset.id}`}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareAssetUrl);
      setCopied(true);
      trackAssetEvent(code, asset.id, "copy");
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const onDownload = () => {
    trackAssetEvent(code, asset.id, "download");
  };

  const onShare = (channel) => {
    const post = (channel === "linkedin" ? linkedinCopy : twitterCopy) || `${shareUrl}`;
    const href = buildShareLink(channel, `${post}\n${shareAssetUrl}`, shareAssetUrl);
    if (!href) return;
    trackAssetEvent(code, asset.id, "share", channel);
    window.open(href, "_blank", "noopener,noreferrer,width=620,height=720");
  };

  const isLogo = asset.kind === "logo";
  const previewBg = isLogo
    ? "bg-[conic-gradient(at_0%_0%,#1f1f1f_25%,#171717_25%,#171717_50%,#1f1f1f_50%,#1f1f1f_75%,#171717_75%)] bg-[length:24px_24px]"
    : "bg-black";
  return (
    <div className="bg-[#141414] border border-zinc-800 overflow-hidden" data-testid={`kit-asset-${asset.id}`}>
      <div className={`${previewBg} flex items-center justify-center aspect-square overflow-hidden`}>
        <img
          src={previewUrl}
          alt={asset.label}
          loading="lazy"
          className={isLogo ? "max-w-[85%] max-h-[60%] object-contain" : "w-full h-full object-cover"}
        />
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="min-w-0">
            <p className="text-sm font-bold truncate text-white" title={asset.label}>{asset.label}</p>
            <p className="text-[10px] text-zinc-500 font-mono uppercase">
              {asset.dimensions} · {asset.mime?.split("/")?.[1] || ""}
              {asset.locale && asset.locale !== "universal" ? ` · ${asset.locale.toUpperCase()}` : ""}
            </p>
          </div>
        </div>

        {/* Personalize toggle — creatives only */}
        {canPersonalise && (
          <button
            type="button"
            onClick={() => setPersonalised(v => !v)}
            data-testid={`kit-asset-personalize-${asset.id}`}
            className={`w-full mb-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 border ${personalised ? "bg-[#FFE600] text-black border-[#FFE600]" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500"}`}
          >
            <Sparkles className="w-3 h-3" />
            {personalised ? t.personal_on : t.personalize}
          </button>
        )}

        <div className="flex gap-2">
          <a
            href={downloadUrl}
            download={asset.filename}
            onClick={onDownload}
            className="flex-1 px-3 py-2 bg-[#FFE600] text-black hover:bg-[#fff066] text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5"
            data-testid={`kit-asset-download-${asset.id}`}
          >
            <Download className="w-3 h-3" /> {t.download}
          </a>
          <button
            type="button"
            onClick={copyUrl}
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-[11px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 text-zinc-200"
            data-testid={`kit-asset-copy-${asset.id}`}
            title={t.copy_url}
          >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>

        {/* Share row — one-tap social posts */}
        <div className="flex gap-1.5 mt-2" data-testid={`kit-asset-share-${asset.id}`}>
          <ShareBtn onClick={() => onShare("x")} icon={Twitter} label="X" testid={`kit-asset-share-${asset.id}-x`} />
          <ShareBtn onClick={() => onShare("linkedin")} icon={Linkedin} label="LinkedIn" testid={`kit-asset-share-${asset.id}-linkedin`} />
          <ShareBtn onClick={() => onShare("whatsapp")} icon={Share2} label="WhatsApp" testid={`kit-asset-share-${asset.id}-whatsapp`} />
          <ShareBtn onClick={() => onShare("telegram")} icon={Send} label="Telegram" testid={`kit-asset-share-${asset.id}-telegram`} />
        </div>

        {/* AI Copy Coach trigger */}
        {canCoach && (
          <button
            type="button"
            onClick={() => setCoachOpen(true)}
            data-testid={`kit-asset-coach-${asset.id}`}
            className="w-full mt-2 px-3 py-1.5 bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/40 hover:border-violet-400 text-violet-300 hover:text-violet-200 text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            <Wand2 className="w-3 h-3" /> {t.coach_btn}
          </button>
        )}
      </div>
      {coachOpen && (
        <CopyCoachModal
          asset={asset}
          code={code}
          t={t}
          onClose={() => setCoachOpen(false)}
        />
      )}
    </div>
  );
}

function ShareBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="flex-1 px-2 py-1.5 bg-[#0f0f0f] hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white inline-flex items-center justify-center"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function CopyCoachModal({ asset, code, t, onClose }) {
  const [channel, setChannel] = useState(asset.kind === "logo" ? "linkedin" : "x");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(-1);

  const generate = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${API}/affiliate/copy-coach`, {
        code, asset_id: asset.id, channel,
      });
      setResult(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "daily_quota_exceeded") {
        setError(t.coach_quota_exceeded);
      } else {
        setError(t.coach_error);
      }
    } finally { setLoading(false); }
  };

  const onCopy = async (variant, i) => {
    try {
      await navigator.clipboard.writeText(variant.text);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(-1), 1500);
      if (result?.caption_id) {
        axios.post(`${API}/affiliate/copy-coach/use`, {
          caption_id: result.caption_id, code, variant_index: i, action: "copy",
        }).catch(() => {});
      }
    } catch { /* ignore */ }
  };

  const onShareVariant = (variant, i) => {
    const enc = encodeURIComponent(variant.text);
    const url = channel === "x"
      ? `https://twitter.com/intent/tweet?text=${enc}`
      : channel === "linkedin"
        ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://toprankpulse.com/r/" + code)}`
        : null;
    if (result?.caption_id) {
      axios.post(`${API}/affiliate/copy-coach/use`, {
        caption_id: result.caption_id, code, variant_index: i, action: "share",
      }).catch(() => {});
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer,width=620,height=720");
  };

  return (
    <div
      role="dialog"
      data-testid="copy-coach-modal"
      className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0f0f0f] border-2 border-violet-500/40 max-w-2xl w-full my-8">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-black uppercase tracking-wider text-white">{t.coach_h}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="copy-coach-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5">
          <p className="text-xs text-zinc-400 mb-1">{t.coach_for}: <span className="text-white font-bold">{asset.label}</span></p>
          <p className="text-[10px] text-zinc-500 font-mono mb-4">{t.coach_disclaimer}</p>

          <div className="flex items-center gap-2 mb-4">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{t.coach_channel}:</p>
            {["x", "linkedin", "instagram"].map(c => (
              <button key={c} type="button"
                onClick={() => setChannel(c)}
                data-testid={`copy-coach-channel-${c}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border ${channel === c ? "bg-violet-500 text-white border-violet-500" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500"}`}>
                {c === "x" ? "X" : c === "linkedin" ? "LinkedIn" : "Instagram"}
              </button>
            ))}
          </div>

          {!result && (
            <button type="button" onClick={generate} disabled={loading}
              className="w-full px-4 py-3 bg-violet-500 hover:bg-violet-400 text-white text-sm font-black uppercase tracking-wider disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="copy-coach-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? t.coach_generating : t.coach_generate}
            </button>
          )}

          {error && <p className="text-xs text-red-400 mt-3" data-testid="copy-coach-error">{error}</p>}

          {result && (
            <div className="space-y-3 mt-2" data-testid="copy-coach-results">
              <p className="text-[10px] font-mono text-zinc-500 uppercase">
                {t.coach_quota_left.replace("{n}", result.quota_remaining)}
              </p>
              {result.variants.map((v, i) => (
                <div key={i} className="bg-[#141414] border border-zinc-800 p-3" data-testid={`copy-coach-variant-${i}`}>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-violet-400 mb-2">{v.angle}</p>
                  <textarea
                    readOnly
                    value={v.text}
                    rows={Math.min(8, Math.max(3, Math.ceil(v.text.length / 80)))}
                    className="w-full bg-transparent text-sm text-zinc-100 resize-none focus:outline-none font-mono whitespace-pre-wrap"
                  />
                  <div className="flex gap-2 mt-2">
                    <button type="button" onClick={() => onCopy(v, i)}
                      data-testid={`copy-coach-copy-${i}`}
                      className="flex-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 text-zinc-100">
                      {copiedIdx === i ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      {copiedIdx === i ? t.copied : t.copy}
                    </button>
                    {(channel === "x" || channel === "linkedin") && (
                      <button type="button" onClick={() => onShareVariant(v, i)}
                        data-testid={`copy-coach-share-${i}`}
                        className="px-3 py-1.5 bg-violet-500 hover:bg-violet-400 text-white text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                        {channel === "x" ? <Twitter className="w-3 h-3" /> : <Linkedin className="w-3 h-3" />}
                        {t.share}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" onClick={generate} disabled={loading}
                className="w-full px-3 py-2 bg-transparent border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 text-[10px] font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5"
                data-testid="copy-coach-regenerate">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {t.coach_regenerate}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SmartSuggestions({ code, t, shareUrl, partnerName, twitterCopy, linkedinCopy }) {
  const [state, setState] = useState({ loading: true, suggestions: [], source: "", segment: "" });
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    axios.get(`${API}/affiliate/suggest/${encodeURIComponent(code)}?limit=3`)
      .then(r => { if (!cancelled) setState({ loading: false, ...r.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, suggestions: [], source: "", segment: "" }); });
    return () => { cancelled = true; };
  }, [code]);

  if (state.loading) return null;
  const list = state.suggestions || [];
  if (list.length === 0) return null;

  const subKey = state.source === "segment"
    ? t.sec_suggest_segment
    : state.source === "cold_start"
      ? t.sec_suggest_cold
      : t.sec_suggest_global;

  return (
    <div className="mb-10" data-testid="kit-suggestions">
      <div className="flex items-baseline gap-3 flex-wrap mb-1">
        <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-black font-['Cabinet_Grotesk']">
          <Sparkles className="w-5 h-5 text-[#FFE600]" /> {t.sec_suggest}
        </h2>
        {state.source === "segment" && state.segment && (
          <span className="px-2 py-0.5 bg-[#FFE600] text-black text-[9px] font-black uppercase tracking-wider rounded-sm" data-testid="kit-suggestions-segment">
            {state.segment}
          </span>
        )}
      </div>
      <p className="text-sm text-zinc-400 mb-4">{subKey}</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((a, i) => (
          <div key={a.id} className="relative" data-testid={`kit-suggestion-${a.id}`}>
            {i === 0 && (
              <span className="absolute z-10 -top-2 left-3 px-2 py-0.5 bg-[#FFE600] text-black text-[9px] font-black uppercase tracking-wider">
                {t.suggest_winner}
              </span>
            )}
            {a.conv_eur > 0 && (
              <span className="absolute z-10 -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-black text-[9px] font-black uppercase tracking-wider"
                data-testid={`kit-suggestion-${a.id}-earned`}
                title={`${a.conv_count || 0} ${t.suggest_conv || "conv."}${a.eur_per_share ? " · €" + Number(a.eur_per_share).toFixed(2) + "/share" : ""}`}>
                €{Number(a.conv_eur).toFixed(2)} {t.suggest_earned_label}
              </span>
            )}
            {a.eur_per_share > 0 && a.shares >= 2 && (
              <span className="absolute z-10 -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-violet-500 text-white text-[9px] font-black uppercase tracking-wider"
                data-testid={`kit-suggestion-${a.id}-eps`}
                title={t.suggest_eps_tip}>
                €{Number(a.eur_per_share).toFixed(2)}/share
              </span>
            )}
            {a.you_used && (
              <span className="absolute z-10 -top-2 right-3 px-2 py-0.5 bg-zinc-200 text-black text-[9px] font-black uppercase tracking-wider">
                {t.suggest_used}
              </span>
            )}
            <AssetCard
              asset={a}
              t={t}
              code={code}
              shareUrl={shareUrl}
              partnerName={partnerName}
              twitterCopy={twitterCopy}
              linkedinCopy={linkedinCopy}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function CopyRow({ label, url, accent, t, testid }) {
  const [copied, setCopied] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <button type="button" onClick={click}
      className="flex items-center justify-between gap-2 px-3 py-2.5 bg-[#141414] border border-zinc-800 hover:border-zinc-500 transition-colors text-left"
      data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        {accent && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent }} />}
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</p>
          <p className="text-xs font-mono text-zinc-200 truncate">{url}</p>
        </div>
      </div>
      <span className="text-xs text-zinc-500 flex-shrink-0">
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </span>
    </button>
  );
}

function CopyGroup({ label, items, t, prefix }) {
  return (
    <div className="mb-5">
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">{label}</p>
      <div className="space-y-2">
        {items.map((s, i) => (
          <CopyArea key={i} label="" content={s} t={t} compact testid={`${prefix}-${i}`} />
        ))}
      </div>
    </div>
  );
}

function CopyArea({ label, content, t, compact, testid }) {
  const [copied, setCopied] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="bg-[#141414] border border-zinc-800" data-testid={testid}>
      {label && <p className="px-3 pt-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</p>}
      <div className="flex gap-2 items-stretch">
        <textarea
          readOnly
          value={content}
          rows={compact ? 2 : 6}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-zinc-100 resize-none focus:outline-none font-mono"
        />
        <button type="button" onClick={click}
          className="px-3 bg-zinc-900 hover:bg-zinc-800 text-xs font-bold uppercase border-l border-zinc-800 flex flex-col items-center justify-center gap-1">
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-zinc-400" />}
          <span className="text-[10px]">{copied ? t.copied : t.copy}</span>
        </button>
      </div>
    </div>
  );
}
